import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import colors from '../../theme/colors';

const XPContainer = styled.div`
  position: absolute;
  top: 20px;
  left: 20px;
  z-index: 1000;
  background: ${colors.base.themePrimary};
  border: 1px solid ${colors.base.themeSecondaryTransparent};
  border-radius: 30px;
  padding: 12px 16px;
  width: 250px;
  box-shadow: ${colors.effects.shadowHover};
  transition: top 0.3s ease, left 0.3s ease;
  overflow: visible;

  /* Collapsed: slide left but keep a handle visible */
  --xpHandleVisible: 0px;
  left: ${props => props.$collapsed ? 'calc(-1 * (var(--xpWidth) - var(--xpHandleVisible)) + 20px)' : '7px'};
  --xpWidth: 250px;

  /* Bring XP + its handle above other overlays when last clicked */
  &:focus-within {
    z-index: 1200;
  }

  /* Mobile: smaller + tighter margins */
  @media (max-width: 768px), (max-height: 768px) {
    top: 10px;
    width: 130px;
    --xpHandleVisible: 0px;
    left: ${props => props.$collapsed ? 'calc(-1 * (var(--xpWidth) - var(--xpHandleVisible)) + 10px)' : '10px'};
    --xpWidth: 130px;
    padding: 10px 14px;
  }
`;

const XPToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 50%;
  right: -52px;
  transform: translateY(-50%);
  width: 40px;
  height: 40px;
  background: ${colors.primary.darkRgba};
  border: none;
  border-radius: 50%;
  color: white;
  cursor: pointer;
  padding: 0;
  box-shadow:
    0 10px 24px rgba(0, 0, 0, 0.35),
    inset 0 1px 0 rgba(255, 255, 255, 0.12);
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;

  /* light contour like Read Me */
  border: 1px solid ${colors.button.aboutBorder};
  user-select: none;

  svg {
    width: 18px;
    height: 18px;
    stroke: rgba(255, 255, 255, 0.95);
    stroke-width: 2.6;
    fill: none;
    stroke-linecap: round;
    stroke-linejoin: round;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.5));
  }

  @media (max-width: 768px), (max-height: 768px) {
    width: 26px;
    height: 26px;
    right: -30px;
    
    svg {
      width: 14px;
      height: 14px;
    }
  }

  &:hover {
    background: ${colors.button.aboutBgHover};
    border-color: ${colors.border.lightHover};
    transform: translateY(-50%) scale(1.12);
    box-shadow: ${colors.effects.shadowLarge};
  }

  &:active {
    transform: translateY(-50%) scale(0.95);
  }
`;

const XPLabel = styled.div`
  color: ${colors.text.light};
  font-size: 0.85rem;
  font-weight: 600;
  margin-bottom: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 768px), (max-height: 768px) {
    font-size: 0.75rem;
    margin-bottom: 6px;
  }
`;

const XPStats = styled.span`
  color: ${colors.base.accentPrimary};
  font-weight: 700;
  font-size: 0.9rem;

  @media (max-width: 768px), (max-height: 768px) {
    font-size: 0.8rem;
  }
`;

const XPBarTrack = styled.div`
  width: 100%;
  height: 20px;
  background: ${colors.background.black30};
  border-radius: 10px;
  overflow: hidden;
  position: relative;
  border: 1px solid ${colors.border.light};

  @media (max-width: 768px), (max-height: 768px) {
    height: 16px;
  }
`;

const XPBarFill = styled(motion.div)`
  height: 100%;
  background: linear-gradient(135deg, ${colors.base.accentPrimary}, ${colors.base.accentSecondary});
  border-radius: 10px;
  position: relative;
`;

/**
 * XP Progress Bar Component
 * Shows user progress based on completed nodes
 * Only visible when user is authenticated
 */
const XPProgressBar = ({ completedNodes, totalNodes, isAuthenticated }) => {
  // Hidden by default — the handle stays visible to slide it out
  const [collapsed, setCollapsed] = useState(true);

  // Don't render if user is not authenticated
  if (!isAuthenticated) return null;

  // Calculate progress
  const completed = completedNodes ? completedNodes.size : 0;
  const total = totalNodes || 1; // Avoid division by zero
  const percentage = Math.round((completed / total) * 100);

  return (
    <XPContainer $collapsed={collapsed}>
      <XPToggle
        onClick={() => setCollapsed(v => !v)}
        title={collapsed ? 'Show XP bar' : 'Hide XP bar'}
        aria-label={collapsed ? 'Show XP bar' : 'Hide XP bar'}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={{ transform: `rotate(${collapsed ? -90 : 90}deg)` }}>
          <path d="M6 9l6 6 6-6" />
        </svg>
      </XPToggle>
      <XPLabel>
        <span>XP Progress</span>
        <XPStats>{percentage}%</XPStats>
      </XPLabel>
      <XPBarTrack>
        <XPBarFill
          animate={{ width: `${percentage}%` }}
          transition={{ duration: 0.5, ease: "easeOut" }}
        />
      </XPBarTrack>
    </XPContainer>
  );
};

export default XPProgressBar;
